import React from "react";
import { motion } from "framer-motion";
import { Phone, CalendarCheck2, ShieldCheck, Users, MapPin, Sparkles } from "lucide-react";

const HERO_BG =
  "https://images.unsplash.com/photo-1618418721668-0d1f72aa4bab?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBjYXIlMjBoZWFkbGlnaHQlMjBkYXJrfGVufDB8fHx8MTc4MTY0NDAyOXww&ixlib=rb-4.1.0&q=85";
const PHONE = "+420 775 659 129";

export default function Hero() {
  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative isolate overflow-hidden bg-[#0A0D17] text-white"
    >
      <div className="absolute inset-0 -z-10">
        <img
          src={HERO_BG}
          alt="STK a emise Znojmo bez starostí — detail prémiového vozu"
          className="h-full w-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0D17]/96 via-[#0A0D17]/82 to-[#0A0D17]/30" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0D17]/40 via-transparent to-[#0A0D17]" />
        <div className="absolute inset-0" style={{
          background: "radial-gradient(circle at 75% 35%, rgba(212,175,55,0.18), transparent 55%), radial-gradient(circle at 15% 80%, rgba(13,27,42,0.7), transparent 60%)"
        }} />
        <div className="absolute inset-0 hero-grid" />
        <div className="absolute inset-0 grain opacity-40" />
      </div>

      <div className="absolute left-0 right-0 top-20 h-px bg-gradient-to-r from-transparent via-amber-300/30 to-transparent" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 pt-36 pb-28 lg:pt-44 lg:pb-40">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <noscript>
              <p>STK Komfort Znojmo — STK, emise, evidenční kontroly, přepisy vozidel, převozní značky, pojištění, autoservis, DPF, odtah a další auto služby pro Znojmo a okolí. Telefon: +420 775 659 129.</p>
            </noscript>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 rounded-full border border-amber-300/25 bg-amber-300/5 px-4 py-1.5 backdrop-blur-sm"
              data-testid="hero-badge"
            >
              <Sparkles className="h-3.5 w-3.5 text-amber-300" />
              <span className="text-xs font-medium tracking-wide text-amber-100">
                Auto concierge ve Znojmě — vše zařídíme za vás
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="font-heading mt-7 text-5xl sm:text-6xl lg:text-[5.25rem] font-extrabold leading-[0.95] text-balance"
              data-testid="hero-title"
            >
              STK a emise
              <span className="block mt-1">ve Znojmě</span>
              <span className="block mt-1 bg-gradient-to-b from-amber-200 via-amber-300 to-amber-500 bg-clip-text text-transparent">
                bez starostí
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.25 }}
              className="font-body mt-7 max-w-2xl text-lg lg:text-xl leading-relaxed text-slate-300"
              data-testid="hero-subtitle"
            >
              Kompletní asistence se STK, emisemi, přepisy, evidenčními kontrolami,
              pojištěním, servisem a péčí o vaše vozidlo. Vše vyřídíme za vás —
              rychle, spolehlivě a s osobním přístupem.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.4 }}
              className="mt-10 flex flex-col sm:flex-row gap-4"
            >
              <a
                href={`tel:${PHONE.replace(/\s+/g, "")}`}
                data-testid="hero-call-lenka-btn"
                className="group inline-flex items-center justify-center gap-3 rounded-xl bg-gradient-to-b from-amber-300 to-amber-500 hover:from-amber-200 hover:to-amber-400 px-7 py-4 font-semibold text-slate-900 shadow-[0_18px_40px_-12px_rgba(212,175,55,0.55)] transition-all duration-200 hover:-translate-y-0.5"
              >
                <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-900/15">
                  <Phone className="h-4 w-4" />
                </span>
                <span className="flex flex-col items-start leading-tight">
                  <span className="text-[10px] uppercase tracking-[0.2em] text-slate-900/70">
                    Zavolat Lence
                  </span>
                  <span className="text-base font-bold">{PHONE}</span>
                </span>
              </a>

              <a
                href="/objednavka"
                data-testid="hero-order-btn"
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-amber-300/30 bg-white/[0.04] px-7 py-4 font-semibold text-white backdrop-blur-sm transition-all hover:bg-amber-300/10 hover:border-amber-300/50 hover:-translate-y-0.5"
              >
                <CalendarCheck2 className="h-4 w-4 text-amber-300" />
                Objednat službu
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.55 }}
              className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-2xl"
            >
              <TrustChip icon={<ShieldCheck className="h-4 w-4 text-amber-300" />} title="Rychle a spolehlivě" />
              <TrustChip icon={<Users className="h-4 w-4 text-amber-300" />} title="Osobní přístup" />
              <TrustChip icon={<CalendarCheck2 className="h-4 w-4 text-amber-300" />} title="Vše zařídíme za vás" />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.7 }}
              className="mt-8 inline-flex items-center gap-3 text-xs text-slate-400"
            >
              <MapPin className="h-3.5 w-3.5 text-amber-300/80" />
              Sedlešovice 34, Nový Šaldorf — Znojmo a okolí
            </motion.div>
          </div>

          {/* Right: brand seal card with logo */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.35 }}
            className="lg:col-span-5"
            data-testid="hero-brand-card"
          >
            <div className="relative rounded-3xl border border-amber-300/15 bg-white/[0.03] p-1.5 backdrop-blur-xl shadow-[0_30px_80px_-30px_rgba(0,0,0,0.7)]">
              <div className="rounded-[22px] bg-gradient-to-br from-[#0D1428] via-[#0A0D17] to-[#0A0D17] p-8">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] uppercase tracking-[0.25em] text-amber-300/80">
                    STK Komfort Znojmo™
                  </span>
                  <span className="flex items-center gap-2 text-xs text-emerald-300">
                    <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
                    online
                  </span>
                </div>

                <div className="mt-4 flex justify-center">
                  <img
                    src="/brand/logo.png"
                    alt="STK Komfort Znojmo"
                    className="w-full max-w-[280px] aspect-square object-contain drop-shadow-[0_10px_30px_rgba(212,175,55,0.25)]"
                  />
                </div>

                <div className="mt-2 h-px w-full bg-gradient-to-r from-transparent via-amber-300/40 to-transparent" />

                <ul className="mt-6 space-y-3 text-sm">
                  <RowStat label="Vyřízených STK" value="3 200+" />
                  <RowStat label="Přepisů vozidel" value="1 100+" />
                  <RowStat label="Spokojených klientů" value="98 %" />
                </ul>

                <div className="mt-6 rounded-xl bg-amber-300/8 border border-amber-300/20 p-4 text-sm text-amber-100/90">
                  &bdquo;Klientka přijde, podepíše plnou moc — a my se postaráme o zbytek.&ldquo;
                  <div className="mt-2 text-xs text-amber-300/80">— Lenka Garguláková</div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function TrustChip({ icon, title }) {
  return (
    <div className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3 backdrop-blur-sm">
      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-300/10 border border-amber-300/20">
        {icon}
      </div>
      <span className="text-sm font-semibold text-white leading-tight">{title}</span>
    </div>
  );
}

function RowStat({ label, value }) {
  return (
    <li className="flex items-center justify-between">
      <span className="text-slate-400">{label}</span>
      <span className="font-heading text-base font-bold text-amber-200">{value}</span>
    </li>
  );
}
